import React, { useState, useEffect, useRef } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography,
    IconButton, Button, Checkbox, Chip, Zoom, Stack, Grid, Paper,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import EnhancedDataGrid from '../Common/EnhancedDataGrid';
import { GenericMessageDialog } from '../Common/Components/GenericMessageDialog';
import { httpGet } from '../../HttpRequestHandling/httpRequestHandler';
import { toast } from 'react-toastify';
import { formatNumber, getDisplayName, stringIsNullOrEmpty } from '../Common/Utilities';
import { isApprovalLevelBreached } from '../InvoiceDocument/InvoiceDocumentEditorFunctions';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Zoom in={true} ref={ref} {...props} />;
});

// Mirrors the amount-typing/formatting behavior of EditableAmountCell in
// InvoiceDocumentEditorGrid.js (Invoice Net Amount field): allows transient
// '.'/'-' while typing, only commits complete numbers, and displays a
// comma-formatted value once the field loses focus.
const AllocAmountField = ({ id, value, onCommit, disabled, error, tooltipTitle, max }) => {
    const inputRef = React.useRef(null);
    const [localError, setLocalError] = React.useState('');
    // 'text' mode shows the comma-formatted display value (blurred state).
    // 'number' mode shows the raw text exactly as the user typed it, with no
    // reformatting/rewriting on every keystroke - this is required so the
    // input's native cursor handling is never fought with a React re-render
    // that swaps in a differently-shaped string (which previously caused the
    // caret to land in the wrong place and made it look like keystrokes were
    // being dropped/skipped).
    const [mode, setMode] = React.useState('text');
    const toFixedOrZero = (v) => {
        const parsed = Number(v);
        return Number.isFinite(parsed) ? parsed.toFixed(2) : '0.00';
    };
    const [rawValue, setRawValue] = React.useState(() => toFixedOrZero(value));
    const lastCommittedValueRef = React.useRef(Number(value) || 0);

    React.useEffect(() => {
        if (mode === 'number') return;
        lastCommittedValueRef.current = Number(value) || 0;
        setRawValue(toFixedOrZero(value));
    }, [value]);

    const isCompleteNumber = (v) => /^-?(?:\d+|\d*\.\d+)$/.test(v);
    const isPartialNumber = (v) => v === '' || v === '-' || v === '.' || v === '-.' || /^-?\d*\.?\d*$/.test(v);

    const hasMax = max !== undefined && max !== null && !isNaN(max);
    const clampToMax = (num) => (hasMax && num > max ? max : num);
    const maxErrorMessage = hasMax ? `Allocated amount cannot exceed ${formatNumber(max)}` : '';
    const fieldHasError = Boolean(error || localError);
    const showOverLimitToast = () => {
        if (maxErrorMessage) {
            toast.error(maxErrorMessage, { toastId: `${id}-allocation-over-limit` });
        }
    };

    const handleFocus = () => {
        if (disabled) return;
        setMode('number');
        setRawValue(toFixedOrZero(rawValue));
    };

    const handlePaste = (e) => {
        if (disabled) return;
        e.preventDefault();
        const pasteText = e.clipboardData.getData('text') || '';
        const cleaned = pasteText.replace(/[^0-9.\-]/g, '');
        if (!cleaned) return;

        setMode('number');
        setRawValue(cleaned);

        if (isCompleteNumber(cleaned)) {
            const numericValue = Number(cleaned);
            if (hasMax && numericValue > max) {
                setLocalError(maxErrorMessage);
                showOverLimitToast();
                return;
            }
            setLocalError('');
            lastCommittedValueRef.current = numericValue;
            onCommit(numericValue);
        }
    };

    // While the user is actively typing, only filter out characters that can
    // never be part of a valid number. Do NOT rewrite/reformat or clamp the
    // displayed text here - doing so replaces the string with a different
    // (often shorter/longer) one on every keystroke, which desyncs the
    // input's cursor position from what the user just typed. Clamping to
    // `max` is deferred to blur so it can never interrupt typing.
    const handleChange = (e) => {
        const val = e.target.value;

        if (!isPartialNumber(val)) {
            return;
        }

        setRawValue(val);

        if (isCompleteNumber(val)) {
            const numericValue = Number(val);
            if (hasMax && numericValue > max) {
                setLocalError(maxErrorMessage);
                showOverLimitToast();
                return;
            }
            setLocalError('');
            lastCommittedValueRef.current = numericValue;
            onCommit(numericValue);
        }
    };

    const handleBlur = () => {
        let val = rawValue;
        if (val === '' || val === '-' || val === '.' || val === '-.') {
            val = '0';
        }
        let num = Number(val);
        if (!isNaN(num) && hasMax && num > max) {
            const previousValue = lastCommittedValueRef.current;
            setRawValue(toFixedOrZero(previousValue));
            setMode('text');
            setLocalError('');
            return;
        }
        if (!isNaN(num)) {
            num = clampToMax(num);
        }
        setLocalError('');
        const fixed = isNaN(num) ? '0.00' : Number(num).toFixed(2);
        setRawValue(fixed);
        setMode('text');
        const committedValue = Number(fixed);
        lastCommittedValueRef.current = committedValue;
        onCommit(committedValue);
    };

    // formatNumber ultimately relies on Number.prototype.toLocaleString for
    // comma grouping; passing it a string (rawValue is always stored as a
    // string here) silently falls back to String.prototype.toLocaleString,
    // which ignores the currency/format options and returns the string
    // unchanged (e.g. "7821.28" instead of "7,821.28"). Convert to a Number
    // first so the formatting actually applies.
    const displayValue = mode === 'number' && !disabled ? rawValue : (formatNumber(Number(rawValue) || 0) || '0.00');

    const field = (
        <input
            id={id}
            ref={inputRef}
            type="text"
            aria-invalid={fieldHasError ? 'true' : 'false'}
            className={`MuiInputBase-input MuiInput-input${fieldHasError ? ' MuiInput-input-error' : ''}`}
            value={displayValue}
            maxLength={15}
            disabled={disabled}
            placeholder="0.00"
            onFocus={handleFocus}
            onBlur={handleBlur}
            onChange={handleChange}
            onPaste={handlePaste}
            style={{
                width: 100,
                textAlign: 'right',
                fontSize: '11px',
                padding: '4px 6px',
                borderRadius: 4,
                border: `1px solid ${fieldHasError ? '#ef4444' : '#ced4da'}`,
                backgroundColor: disabled ? '#f3f4f6' : '#fff',
                color: '#111827',
                outline: 'none'
            }}
            onKeyDown={(e) => {
                if (e.ctrlKey || e.metaKey) return;
                const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Enter'];
                if (!/[0-9.\-]/.test(e.key) && !allowedKeys.includes(e.key)) {
                    e.preventDefault();
                }
            }}
        />
    );

    return (
        <Tooltip
            title={localError || tooltipTitle || ''}
            disableHoverListener={!(localError || tooltipTitle)}
            disableFocusListener={!(localError || tooltipTitle)}
            disableTouchListener={!(localError || tooltipTitle)}
            componentsProps={localError ? {
                tooltip: {
                    sx: {
                        bgcolor: '#d32f2f',
                        color: '#fff'
                    }
                }
            } : undefined}
        >
            <span>{field}</span>
        </Tooltip>
    );
};

const isAmountOverBudget = (requested, allowed, amount, flag) => flag === 'Y' && amount < 0 ? requested < allowed : requested > allowed;
const isBudgetFullyConsumed = (remaining, amount, flag) => flag === 'Y' && amount < 0 ? remaining >= 0 : remaining <= 0;

export function PlacementLevelLookupDialog(props) {
    const { info, onClose } = props;

    const canEditAmountByRule = typeof info.isEditableByRule === 'function'
        ? info.isEditableByRule('Invoice Net Amount')
        : info.isEditableByRule !== false;

    // --- State ---
    const [openConfirm, setOpenConfirm] = useState(false);
    const [openResetAllConfirm, setOpenResetAllConfirm] = useState(false);
    const [lockedIOs, setLockedIOs] = useState(new Set());
    const [ioData, setIoData] = useState([]);
    const [selectedIOs, setSelectedIOs] = useState([]);
    const [loadingIOs, setLoadingIOs] = useState(new Set());
    // When in placement mode with placements showing, the IO panel can be fully
    // collapsed so only the Placement(s) panel is visible (mutually exclusive -
    // both panels are never shown expanded at the same time in placement mode).
    // Defaults to visible so IO mode / no-selection behavior is unaffected.
    const [showIOPanel, setShowIOPanel] = useState(true);
    // Placement-level dialog requirement: only one IO can be selected at a time.
    const canSelectMultiple = false;

    const normalizeMode = (mode) => {
        const value = (mode || '').toString().trim().toLowerCase();
        if (value === 'io' || value === 'default') return 'io';
        if (value === 'placement' || value === 'partial') return 'placement';
        return null;
    };

    const getLineMode = (line) => {
        const explicitMode = normalizeMode(line?.ioSessionMode || line?.placementSessionMode);
        if (explicitMode) return explicitMode;

        const hasIoCode = !stringIsNullOrEmpty(line?.ioNumber || line?.poCode);

        // Backward-compatibility fallback:
        // if an IO is already selected on the line but no mode was persisted,
        // always treat it as IO mode (entire IO) rather than leaving mode null.
        if (hasIoCode) return 'io';

        return null;
    };

    const getInitialMode = () => {
        const existingLines = info.existingDetailLines || [];
        const selectedSeqIds = info.selectedRow ? info.selectedRow.map(r => r.seqId) : [];

        // 1. Check selected rows first
        for (const line of existingLines) {
            const selectedMode = getLineMode(line);
            if (selectedSeqIds.includes(line.seqId) && selectedMode) {
                return selectedMode;
            }
        }

        // 2. Fallback: Top-down search for the first assigned mode
        for (const line of existingLines) {
            const lineMode = getLineMode(line);
            if (lineMode) {
                return lineMode;
            }
        }

        // 3. Fallback to the overall document session mode, then prop, or null
        return normalizeMode(info.ioSessionMode || info.documentSessionMode || info.placementSessionMode);
    };

    const initialSessionMode = getInitialMode();

    const [sessionMode, setSessionMode] = useState(initialSessionMode);
    const [placementsCache, setPlacementsCache] = useState({});
    const placementsCacheRef = useRef({});
    const refreshInFlightRef = useRef(false);
    const initInFlightRef = useRef(false);
    const [showAllPlacements, setShowAllPlacements] = useState(false);
    const ioGridRef = useRef(null);
    const ioGridHostRef = useRef(null);
    const ioGridApiRef = useGridApiRef();

    const alignIOGridLayout = React.useCallback(() => {
        if (!showIOPanel) return;

        const hostWidth = ioGridHostRef.current?.getBoundingClientRect?.().width || 0;
        if (hostWidth <= 0) return;

        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                ioGridApiRef.current?.resize?.();
            });
        });
    }, [showIOPanel]);

    useEffect(() => {
        placementsCacheRef.current = placementsCache;
    }, [placementsCache]);

    useEffect(() => {
        if (!info.openDialog) return;
        alignIOGridLayout();
    }, [info.openDialog, showIOPanel, ioData.length, alignIOGridLayout]);

    // The dialog now shows only one panel at a time (IO list OR placements),
    // so draggable split-resizer behavior is intentionally removed.
    const splitContainerRef = useRef(null);
    const [includeAllPO, setIncludeAllPO] = useState(
        info.serviceDateFilterForIoLookup === "OPTIONAL_OVERRIDE" &&
        stringIsNullOrEmpty(info.serviceTermStartDate) &&
        stringIsNullOrEmpty(info.serviceTermEndDate)
    );
    const isPostFailedMode = Number(info.originalInvoiceDocumentStatus) === 180 || ((info.invoicePostStatus || '').toString().trim().toLowerCase() === 'post failed');
    const detailLines = info.existingDetailLines || [];
    const routingNumberOfApprovalLevels = Number(info.routingNumberOfApprovalLevels || 0);
    const isFullyApprovedDetailLine = line => isApprovalLevelBreached({
        routingNumberOfApprovalLevels,
        invoiceDocumentStatusId: Number(line.lineStatus)
    });
    const hasFullyApprovedDetailLine = detailLines.some(isFullyApprovedDetailLine);
    const hasNotFullyApprovedDetailLine = detailLines.some(line => !isFullyApprovedDetailLine(line));
    const hasMixedApprovalStatus = hasFullyApprovedDetailLine && hasNotFullyApprovedDetailLine;

    const getOverallMode = () => {
        const existingLines = info.existingDetailLines || [];
        const selectedSeqIds = info.selectedRow ? info.selectedRow.map(r => r.seqId) : [];
        let mode = null;
        for (const line of existingLines) {
            const lineMode = getLineMode(line);
            if (!selectedSeqIds.includes(line.seqId) && lineMode && !mode) {
                mode = lineMode;
            }
        }
        return mode;
    };
    const overallMode = getOverallMode();
    const hasAnyAssignedMode = (info.existingDetailLines || []).some(line => !!getLineMode(line));
    // Mode is not allowed to be mixed across detail lines. Once any other line has
    // been assigned a mode ('io' or 'placement'), the opposite selection method must
    // be locked here so the user cannot introduce a mixed-mode state. This can only
    // be lifted document-wide via "Clear all & reset".
    const lockedToMode = overallMode || null;
    const effectiveMode = sessionMode || lockedToMode || null;

    const fDate = (dStr) => {
        if (!dStr) return '';

        const raw = dStr.toString().trim();

        const isoDateMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T\s].*)?$/);
        if (isoDateMatch) {
            const [, year, month, day] = isoDateMatch;
            return `${month}/${day}/${year}`;
        }

        const d = new Date(raw);
        if (isNaN(d.getTime())) return raw;

        return d.toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric',
            timeZone: 'UTC'
        });
    };

    const normalizeIOCode = (value) => (value || '').toString().trim().toLowerCase();
    const isSameIOCode = (left, right) => normalizeIOCode(left) === normalizeIOCode(right);
    const getIOModeFromRow = (row) => normalizeMode(row?.ioSessionMode || row?.io_session_mode || row?.placementSessionMode);
    const isPlacementOnlyIO = (row) => getIOModeFromRow(row) === 'placement';
    const isIOModeOnlyIO = (row) => getIOModeFromRow(row) === 'io';
    const getPlacementOnlyIOTooltip = (row) =>
        isPlacementOnlyIO(row)
            ? <React.Fragment>This IO is already assigned to <strong>"Matching by Placement"</strong> and cannot be used in <strong>"Matching by Summary"</strong></React.Fragment>
            : '';
    const getIOModeOnlyTooltip = (row) =>
        isIOModeOnlyIO(row)
            ? <React.Fragment>This IO is already assigned to <strong>"Matching by Summary"</strong> and cannot be used in <strong>"Matching by Placement"</strong></React.Fragment>
            : '';

    const getTargetSelectedIOCodes = () => {
        const existingLines = info.existingDetailLines || [];
        const selectedSeqIds = info.selectedRow ? info.selectedRow.map(r => r.seqId) : [];
        return new Set(
            existingLines
                .filter(x => selectedSeqIds.includes(x.seqId) && x.ioNumber)
                .map(x => normalizeIOCode(x.ioNumber))
        );
    };

    const sortSelectedIOsToTop = (rows, selectedCodes) => {
        const safeRows = [...(rows || [])];
        safeRows.sort((a, b) => {
            const aSelected = selectedCodes.has(normalizeIOCode(a.poCode));
            const bSelected = selectedCodes.has(normalizeIOCode(b.poCode));

            if (aSelected === bSelected) {
                return 0;
            }

            return aSelected ? -1 : 1;
        });
        return safeRows;
    };

    // --- 1. Initialization Logic ---
    useEffect(() => {
        if (info.openDialog) {
            const initialIncludeAllPO = info.serviceDateFilterForIoLookup === "OPTIONAL_OVERRIDE" &&
                stringIsNullOrEmpty(info.serviceTermStartDate) &&
                stringIsNullOrEmpty(info.serviceTermEndDate);
            setIncludeAllPO(initialIncludeAllPO);
            initData(initialIncludeAllPO);
        }
    }, [info.openDialog]);

    const getOtherLinesAllocated = (ioId, placementId) => {
        const existingLines = info.existingDetailLines || [];
        const selectedSeqIds = info.selectedRow ? info.selectedRow.map(r => r.seqId) : [];
        let total = 0;
        for (const line of existingLines) {
            if (selectedSeqIds.includes(line.seqId)) continue;
            if (line.ioNumber === ioId) {
                if (line.placements) {
                    const match = line.placements.find(p => p.placementId === placementId);
                    if (match) total += (parseFloat(match.allocAmount) || 0);
                }
            }
        }
        return total;
    };


    const buildIOListUrl = (includeAllOverride) => {
        const effectiveIncludeAllPO = includeAllOverride !== undefined ? includeAllOverride : includeAllPO;
        let serviceDateFilterApplicable = 1;
        if (info.serviceDateFilterForIoLookup === "NOT_APPLICABLE" || info.modelDefId !== 1) {
            serviceDateFilterApplicable = 0;
        } else if (info.serviceDateFilterForIoLookup === "OPTIONAL_OVERRIDE") {
            serviceDateFilterApplicable = effectiveIncludeAllPO ? 0 : 1;
        }

        const query = new URLSearchParams({
            vendorCode: info.vendorCode || '',
            ClientCode: info.clientCode || 'null',
            serviceDateFilterApplicable: serviceDateFilterApplicable.toString(),
        });
        if (info.serviceTermStartDate && info.serviceTermEndDate) {
            query.set('serviceTermStartDate', info.serviceTermStartDate);
            query.set('serviceTermEndDate', info.serviceTermEndDate);
        }

        const url = `/api/lookup/poheaderlistbyvendor/${info.modelDefId}/${info.invoiceDocumentId || 0}?${query.toString()}`;
        return url;
    };

    const initData = async (includeAllOverride, forceRefreshPlacements = false, manageProgress = true) => {
        // The dialog can be rendered/effected more than once while it opens
        // (especially with React strict-mode development behavior). Do not
        // start a second initialization request or increment the global
        // loading counter twice for the same dialog load.
        if (manageProgress && initInFlightRef.current) return;
        if (manageProgress) initInFlightRef.current = true;
        if (manageProgress && info.inProgress) info.inProgress(true);

        const url = buildIOListUrl(includeAllOverride);

        try {
            const ioResponse = await httpGet(url);
            let allIOs = ioResponse.data || [];

            const existingLines = info.existingDetailLines || [];
            const selectedSeqIds = info.selectedRow ? info.selectedRow.map(r => r.seqId) : [];

            const selectedIONumbers = getTargetSelectedIOCodes();
            allIOs = sortSelectedIOsToTop(allIOs, selectedIONumbers);

            setIoData(allIOs);

            const hydratedSelectionsByCode = new Map();
            const currentlyLocked = new Set();

            // Target lines only (for hydrating dialog state)
            const targetLines = existingLines.filter(line => selectedSeqIds.includes(line.seqId));

            for (const line of targetLines) {
                if (line.ioNumber) {
                    // if (!isPostFailedMode && line.lineStatus !== 80) {
                    //     currentlyLocked.add(line.ioNumber);
                    // }
                    // Already-posted IOs (partial posting re-edit) must remain locked so the
                    // user cannot uncheck/change selection for them.
                    if (info.isPartialPostMode && info.postedIONumbers?.has(line.ioNumber)) {
                        currentlyLocked.add(line.ioNumber);
                    }

                    const ioCodeKey = normalizeIOCode(line.ioNumber);
                    if (hydratedSelectionsByCode.has(ioCodeKey)) {
                        continue;
                    }

                    const matchedIO = info.poHeaderLookup?.find(io => normalizeIOCode(io.poCode) === ioCodeKey) || allIOs.find(i => normalizeIOCode(i.poCode) === ioCodeKey);
                    if (matchedIO) {
                        const pls = await fetchPlacementsApi(line.ioNumber, forceRefreshPlacements);

                        // Map existing line placements to cache
                        const linePls = line.placements || [];
                        const mappedPlacements = pls
                            // Keep consumed placements available for both Nexelus and non-Nexelus systems.
                            .map(p => {
                                const otherAlloc = getOtherLinesAllocated(line.ioNumber, p.placementId);
                                const plRem = (p.amount - (p.consumedAmt || 0)) - otherAlloc;
                                const match = linePls.find(lp => lp.placementId === p.placementId);
                                if (match || normalizeMode(line.ioSessionMode || line.placementSessionMode) === 'io') {
                                    return { ...p, checked: true, allocAmount: (match && match.allocAmount !== undefined) ? match.allocAmount : plRem };
                                }
                                return { ...p, checked: false, allocAmount: 0 };
                            }).filter(p => p.checked);

                        hydratedSelectionsByCode.set(ioCodeKey, {
                            ...matchedIO,
                            mode: normalizeMode(line.ioSessionMode || line.placementSessionMode || info.ioSessionMode || info.placementSessionMode) || 'io',
                            placements: mappedPlacements,
                            allocAmount: mappedPlacements.reduce((s, p) => s + (p.allocAmount || 0), 0)
                        });
                    }
                }
            }
            setLockedIOs(currentlyLocked);
            const hydratedSelections = Array.from(hydratedSelectionsByCode.values());
            setSelectedIOs(hydratedSelections);
            // If reopening an existing placement-mode session that already has
            // selections, collapse the IO panel immediately so only the
            // Placement(s) panel is shown - matching the mutually-exclusive
            // panel behavior used when selecting placements interactively.
            // Checked against the freshly-hydrated selections' own mode (rather
            // than the sessionMode state, which may not yet reflect the
            // just-computed initial mode on the very first hydration pass).
            if (hydratedSelections.some(io => io.mode && io.mode !== 'io')) {
                setShowIOPanel(false);
            }
            return allIOs;
        } finally {
            if (manageProgress) {
                initInFlightRef.current = false;
                if (info.inProgress) info.inProgress(false);
            }
        }
    };

    const fetchPlacementsApi = async (ioId, forceRefresh = false) => {
        const cached = placementsCacheRef.current[ioId];
        if (!forceRefresh && cached) return cached;

        const res = await httpGet(`/api/lookup/placements/${encodeURIComponent(ioId)}/${info.modelDefId}/${info.invoiceDocumentId || 0}`);
        const data = res.data || [];
        setPlacementsCache(prev => {
            const next = { ...prev, [ioId]: data };
            placementsCacheRef.current = next;
            return next;
        });
        return data;
    };

    // --- 2. Action Handlers ---
    const handleCheckboxToggle = async (row, event) => {
        event.stopPropagation();
        if (loadingIOs.has(row.poCode)) return;

        const isSelected = selectedIOs.some(s => isSameIOCode(s.poCode, row.poCode));

        // Unchecking an already-selected IO is equivalent to clicking "Remove" on
        // the right pane, so it must always be allowed (mode/lock restrictions
        // below only apply when adding a NEW selection), otherwise the checkbox
        // gets stuck disabled while the Remove button remains clickable.
        if (isSelected) {
            const next = selectedIOs.filter(s => !isSameIOCode(s.poCode, row.poCode));
            setSelectedIOs(next);
            if (next.length === 0) {
                setSessionMode(null);
                setShowIOPanel(true);
            }
            setLockedIOs(prev => {
                if (!prev.has(row.poCode)) return prev;
                const nextLocked = new Set(prev);
                nextLocked.delete(row.poCode);
                return nextLocked;
            });
            return;
        }

        if ((effectiveMode && effectiveMode !== 'io') || lockedIOs.has(row.poCode) || isPlacementOnlyIO(row)) return;

        {
            if (!sessionMode) setSessionMode('io');
            // IO mode must not auto-select or submit placement rows.
            const newIO = { ...row, mode: 'io', placements: [], allocAmount: 0 };
            setSelectedIOs(prev => canSelectMultiple ? [...prev, newIO] : [newIO]);
        }
    };

    const handleRowClick = async (params, event) => {
        if (event?.target?.closest?.('[data-stop-row-click="true"]')) return;
        const clickedField = event?.target?.closest?.('[role="cell"]')?.getAttribute('data-field');
        if (clickedField !== 'poCode') return;
        const row = params.row;
        if (effectiveMode === 'io' || lockedIOs.has(row.poCode) || loadingIOs.has(row.poCode) || isIOModeOnlyIO(row)) return;

        const isAlreadySelected = selectedIOs.some(s => isSameIOCode(s.poCode, row.poCode));
        if (isAlreadySelected && (!effectiveMode || effectiveMode === 'placement')) {
            removeIO(row.poCode);
            return;
        }

        if (!sessionMode) setSessionMode('placement');
        setLoadingIOs(prev => new Set(prev).add(row.poCode));
        try {
            await fetchPlacementsApi(row.poCode);
            if (!selectedIOs.some(s => isSameIOCode(s.poCode, row.poCode))) {
                const newIO = { ...row, mode: 'placement', placements: [], allocAmount: 0 };
                setSelectedIOs(prev => canSelectMultiple ? [...prev, newIO] : [newIO]);
            }
            // Collapse the IO panel so only the Placement(s) panel is shown -
            // both panels are never expanded simultaneously in placement mode.
            setShowIOPanel(false);
        } finally {
            setLoadingIOs(prev => {
                const next = new Set(prev);
                next.delete(row.poCode);
                return next;
            });
        }
    };

    const togglePlacement = (ioId, placement, checked) => {
        setSelectedIOs(prev => prev.map(io => {
            if (io.poCode !== ioId) return io;
            let updated;
            if (checked) {
                const otherAlloc = getOtherLinesAllocated(ioId, placement.placementId);
                const plRem = (placement.amount - (placement.consumedAmt || 0)) - otherAlloc;
                updated = [...io.placements, { ...placement, checked: true, allocAmount: plRem }];
            } else {
                updated = io.placements.filter(p => p.placementId !== placement.placementId);
            }
            return { ...io, placements: updated, allocAmount: updated.reduce((s, p) => s + (p.allocAmount || 0), 0) };
        }));
    };

    const toggleAllPlacements = (ioId, checked) => {
        const pls = placementsCache[ioId] || [];
        setSelectedIOs(prev => prev.map(io => {
            if (io.poCode !== ioId) return io;
            const visiblePlacements = showAllPlacements
                ? pls
                : pls.filter(p => (p.amount - (p.consumedAmt || 0)) > 0);
            const selectablePlacements = visiblePlacements;
            const updated = checked ? selectablePlacements
                .map(p => {
                    const otherAlloc = getOtherLinesAllocated(ioId, p.placementId);
                    const plRem = (p.amount - (p.consumedAmt || 0)) - otherAlloc;
                    return { ...p, checked: true, allocAmount: plRem };
                }) : [];
            return { ...io, placements: updated, allocAmount: updated.reduce((s, p) => s + (p.allocAmount || 0), 0) };
        }));
    };

    const handleallocAmountChange = (ioId, placementId, value) => {
        const numericValue = parseFloat(value) || 0;
        setSelectedIOs(prev => prev.map(io => {
            if (io.poCode !== ioId) return io;
            const exists = io.placements.some(p => p.placementId === placementId);
            let updated;
            if (exists) {
                updated = io.placements.map(p => p.placementId === placementId ? { ...p, allocAmount: numericValue } : p);
            } else {
                // In IO mode every placement is implicitly selected, so the
                // Alloc. Amount field is directly editable with no separate
                // "checked" gate. Mirror that in placement mode: typing an
                // amount into an unselected placement's field should select it
                // instead of requiring the row checkbox to be ticked first.
                const source = (placementsCache[ioId] || []).find(p => p.placementId === placementId);
                if (!source) return io;
                updated = [...io.placements, { ...source, checked: true, allocAmount: numericValue }];
            }
            return { ...io, placements: updated, allocAmount: updated.reduce((s, p) => s + (p.allocAmount || 0), 0) };
        }));
    };

    const handleReset = () => {
        if (info.isPartialPostMode) {
            return;
        }
        // If any detail line already has a mode assigned, clearing/resetting here
        // must clear the mode for ALL detail lines (not just this dialog's local
        // selection), since mode cannot be mixed across lines. Confirm with the user
        // first because this is a destructive, document-wide action.
        if (hasAnyAssignedMode && typeof info.onResetAllLinesMode === 'function') {
            setOpenResetAllConfirm(true);
            return;
        }
        setSelectedIOs([]);
        setSessionMode(null);
        setLockedIOs(new Set());
        setShowIOPanel(true);
    };

    const confirmResetAllLinesMode = () => {
        setOpenResetAllConfirm(false);
        setSelectedIOs([]);
        setSessionMode(null);
        setLockedIOs(new Set());
        setShowIOPanel(true);
        if (typeof info.onResetAllLinesMode === 'function') {
            info.onResetAllLinesMode();
        }
    };

    const cancelResetAllLinesMode = () => {
        setOpenResetAllConfirm(false);
    };

    const removeIO = (ioId) => {
        // Already-posted IOs (in partial posting re-edit) cannot be removed.
        if (info.isPartialPostMode && info.postedIONumbers?.has(ioId)) return;

        const next = selectedIOs.filter(s => !isSameIOCode(s.poCode, ioId));
        setSelectedIOs(next);
        if (next.length === 0) {
            setSessionMode(null);
            setShowIOPanel(true);
        }

        // Clearing a removed IO from lockedIOs is required, otherwise the row for
        // this IO stays locked (both the checkbox and the row-click placement
        // selection check lockedIOs), leaving both selection methods disabled
        // instead of re-enabling the one matching the current mode.
        setLockedIOs(prev => {
            if (!prev.has(ioId)) return prev;
            const nextLocked = new Set(prev);
            nextLocked.delete(ioId);
            return nextLocked;
        });
    };

    const handleRefresh = async () => {
        const filterText = ioGridRef?.current?.filterText;
        if (stringIsNullOrEmpty(filterText)) return;
        if (refreshInFlightRef.current) return;

        refreshInFlightRef.current = true;
        if (info.inProgress) info.inProgress(true);

        try {
            const syncUrl = `/api/invoicedocument/refetchpoinfo/${info.modelDefId}?poCode=${encodeURIComponent(filterText)}`;
            const syncResponse = await httpGet(syncUrl);
            const syncedPoCode = normalizeIOCode(syncResponse?.data?.poCode || filterText);

            placementsCacheRef.current = {};
            setPlacementsCache({});

            const maxAttempts = 5;
            const delayMs = 1200;
            let found = false;

            for (let attempt = 0; attempt < maxAttempts; attempt++) {
                const rows = await initData(undefined, true, false);
                found = (rows || []).some(r => normalizeIOCode(r.poCode) === syncedPoCode);
                if (found) break;
                if (attempt < maxAttempts - 1) {
                    await new Promise(resolve => setTimeout(resolve, delayMs));
                }
            }

            if (found) {
                toast.success('IO/PO sync completed successfully.');
            } else {
                toast.error('IO/PO sync failed. no matching IO/PO record was found for the entered code, vendor, or service-date filter.');
            }
        } catch (error) {
            const status = error?.response?.status;
            if (status === 404) {
                toast.error('IO/PO sync failed. the entered IO/PO code was not found.');
            } else if (status === 400) {
                toast.error('IO/PO sync failed. invalid IO/PO refresh request.');
            } else {
                toast.error('IO/PO sync failed. an error occurred while refreshing IO/PO data.');
            }
        } finally {
            refreshInFlightRef.current = false;
            if (info.inProgress) info.inProgress(false);
        }
    };

    const applyIncludeAllPOChange = async (isChecked) => {
        setIncludeAllPO(isChecked);

        if (info.inProgress) info.inProgress(true);
        try {
            const url = buildIOListUrl(isChecked);
            const ioResponse = await httpGet(url);

            const existingLines = info.existingDetailLines || [];
            const selectedSeqIds = info.selectedRow ? info.selectedRow.map(r => r.seqId) : [];
            const targetLines = existingLines.filter(line => selectedSeqIds.includes(line.seqId) && line.ioNumber);

            const selectedCodes = getTargetSelectedIOCodes();
            (selectedIOs || []).forEach(io => selectedCodes.add(normalizeIOCode(io.poCode)));

            const targetFallbackRows = targetLines
                .map(line => info.poHeaderLookup?.find(io => isSameIOCode(io.poCode, line.ioNumber)))
                .filter(Boolean);

            const mergedByCode = new Map();
            [...(ioResponse.data || []), ...(selectedIOs || []), ...targetFallbackRows].forEach(row => {
                const key = normalizeIOCode(row?.poCode);
                if (!key || mergedByCode.has(key)) return;
                mergedByCode.set(key, row);
            });

            const mergedRows = Array.from(mergedByCode.values());
            setIoData(sortSelectedIOsToTop(mergedRows, selectedCodes));

            const nextLocked = new Set(lockedIOs);
            const hydratedMissingSelectionsByCode = new Map();

            for (const line of targetLines) {
                // if (!isPostFailedMode && line.lineStatus !== 80) {
                //     nextLocked.add(line.ioNumber);
                // }
                if (info.isPartialPostMode && info.postedIONumbers?.has(line.ioNumber)) {
                    nextLocked.add(line.ioNumber);
                }

                const ioCodeKey = normalizeIOCode(line.ioNumber);
                const alreadySelected = selectedIOs.some(s => isSameIOCode(s.poCode, line.ioNumber));
                if (alreadySelected || hydratedMissingSelectionsByCode.has(ioCodeKey)) continue;

                const matchedIO = mergedRows.find(i => isSameIOCode(i.poCode, line.ioNumber));
                if (!matchedIO) continue;

                const pls = await fetchPlacementsApi(line.ioNumber);
                const linePls = line.placements || [];
                const mappedPlacements = pls
                    .map(p => {
                        const otherAlloc = getOtherLinesAllocated(line.ioNumber, p.placementId);
                        const plRem = (p.amount - (p.consumedAmt || 0)) - otherAlloc;
                        const match = linePls.find(lp => lp.placementId === p.placementId);
                        if (match || normalizeMode(line.ioSessionMode || line.placementSessionMode) === 'io') {
                            return { ...p, checked: true, allocAmount: (match && match.allocAmount !== undefined) ? match.allocAmount : plRem };
                        }
                        return { ...p, checked: false, allocAmount: 0 };
                    })
                    .filter(p => p.checked);

                hydratedMissingSelectionsByCode.set(ioCodeKey, {
                    ...matchedIO,
                    mode: normalizeMode(line.ioSessionMode || line.placementSessionMode || info.ioSessionMode || info.placementSessionMode) || 'io',
                    placements: mappedPlacements,
                    allocAmount: mappedPlacements.reduce((s, p) => s + (p.allocAmount || 0), 0)
                });
            }

            setLockedIOs(nextLocked);
            const hydratedMissingSelections = Array.from(hydratedMissingSelectionsByCode.values());
            if (hydratedMissingSelections.length > 0) {
                setSelectedIOs(prev => {
                    const base = prev || [];
                    const missing = hydratedMissingSelections.filter(item => !base.some(x => isSameIOCode(x.poCode, item.poCode)));
                    if (missing.length === 0) return base;
                    return canSelectMultiple ? [...base, ...missing] : [missing[0]];
                });
                // Same as initData's hydration: if the newly-hydrated selection(s) are
                // in placement mode, collapse the IO panel so only the Placement(s)
                // panel is shown - this path is used when the user checks "Include
                // All IO" to reveal an already-selected IO whose service date falls
                // outside the normal filter range.
                if (hydratedMissingSelections.some(io => io.mode && io.mode !== 'io')) {
                    setShowIOPanel(false);
                }
            }
        } finally {
            if (info.inProgress) info.inProgress(false);
        }
    };

    const handleIncludeAllIOChange = (event) => {
        const isChecked = event.target.checked;
        if (includeAllPO === isChecked) return;

        // Unchecking re-applies the strict service date filter, which can strand IOs/placements
        // already selected in this session, so confirm with the user before resetting them.
        if (!isChecked && selectedIOs.length > 0) {
            setOpenConfirm(true);
            return;
        }

        applyIncludeAllPOChange(isChecked);
    };

    const confirmDisableIncludeAllPO = () => {
        setOpenConfirm(false);
        setSelectedIOs([]);
        setSessionMode(null);
        setLockedIOs(new Set());
        applyIncludeAllPOChange(false);
    };

    const cancelDisableIncludeAllPO = () => {
        setOpenConfirm(false);
    };

    const getIncludeAllIO = () => {
        if (info.serviceDateFilterForIoLookup !== "OPTIONAL_OVERRIDE") return null;
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', fontWeight: 'normal', pr: '10px', pl: '3px', whiteSpace: 'nowrap' }}>
                <Checkbox id="includeAllPOCheckbox" size="small" checked={includeAllPO} onChange={handleIncludeAllIOChange} />
                <Typography sx={{ fontSize: '12px', color: '#374151', whiteSpace: 'nowrap' }}>Include All IO/PO</Typography>
            </Box>
        );
    };

    // --- 3. Grid Definition (Left Pane) ---
    const columns = [
        {
            headerName: ' ', field: 'chk', width: 45, renderCell: p => (
                loadingIOs.has(p.row.poCode) ? (
                    <CircularProgress size={20} />
                ) : (() => {
                    const checkboxDisabled = isPlacementModeActive || lockedIOs.has(p.row.poCode) || isPlacementOnlyIO(p.row);
                    const placementOnlyTooltip = getPlacementOnlyIOTooltip(p.row);
                    return (
                        <Box sx={{ cursor: checkboxDisabled ? 'not-allowed' : 'pointer' }}>
                            <Tooltip
                                title={placementOnlyTooltip}
                                disableHoverListener={!placementOnlyTooltip}
                                disableFocusListener={!placementOnlyTooltip}
                                disableTouchListener={!placementOnlyTooltip}
                            >
                                <span>
                                    <Checkbox
                                        id={`ioSelectCheckbox-${p.row.poCode}`}
                                        size="small"
                                        checked={selectedIOs.some(s => isSameIOCode(s.poCode, p.row.poCode) && normalizeMode(s.mode) !== 'placement')}
                                        indeterminate={selectedIOs.some(s => isSameIOCode(s.poCode, p.row.poCode) && normalizeMode(s.mode) === 'placement')}
                                        disabled={checkboxDisabled}
                                        onClick={(e) => handleCheckboxToggle(p.row, e)}
                                        sx={{
                                            cursor: checkboxDisabled ? 'not-allowed' : 'pointer',
                                            '& .MuiSvgIcon-root': {
                                                cursor: checkboxDisabled ? 'not-allowed' : 'pointer'
                                            }
                                        }}
                                    />
                                </span>
                            </Tooltip>
                        </Box>
                    );
                })()
            )
        },
        {
            headerName: 'IO/PO Number', field: 'poCode', width: 150, renderCell: p => {
                const ioModeOnlyTooltip = getIOModeOnlyTooltip(p.row);
                const isPlacementSelectionBlocked = isIOModeActive || isIOModeOnlyIO(p.row);
                return (
                    <Tooltip
                        title={ioModeOnlyTooltip}
                        disableHoverListener={!ioModeOnlyTooltip}
                        disableFocusListener={!ioModeOnlyTooltip}
                        disableTouchListener={!ioModeOnlyTooltip}
                    >
                        <span>
                            <Box sx={{ pointerEvents: isIOModeActive ? 'none' : 'auto' }}>
                                <Typography
                                    variant="body2"
                                    sx={{ color: isIOModeOnlyIO(p.row) ? 'inherit' : '#2563eb', fontWeight: isIOModeOnlyIO(p.row) ? 'normal' : 500, cursor: isPlacementSelectionBlocked ? 'not-allowed' : 'pointer' }}
                                >
                                    {p.row.poCode}
                                    {Number(p.row.ioHasPlacement) === 1 && !isIOModeOnlyIO(p.row) && (
                                        <Tooltip
                                            title="The IO contains placement line(s)"
                                            arrow
                                            placement="top"
                                            componentsProps={{
                                                tooltip: {
                                                    sx: { fontSize: '0.675rem', lineHeight: 1.3 }
                                                }
                                            }}
                                        >
                                            <Box
                                                component="img"
                                                src="/images/placement_detail_icon.png"
                                                alt="Has Placements"
                                            sx={{ width: 18, height: 18, ml: 0.5, display: 'inline-block', verticalAlign: 'middle', position: 'relative', top: '-2px' }}
                                            />
                                        </Tooltip>
                                    )}
                                </Typography>
                            </Box>
                        </span>
                    </Tooltip>
                );
            }
        },
        { headerName: getDisplayName('site'), field: 'siteName', width: 120 },
        { headerName: 'Service Date', field: 'serviceStartDate', width: 120, renderCell: p => p.row.serviceStartDate || '' },
        { headerName: 'IO/PO Amount', field: 'poAmount', align: 'right', renderCell: p => formatNumber(p.row.poAmount), width: 100 },
        { headerName: 'Consumed Amount', field: 'consumedAmt', align: 'right', renderCell: p => formatNumber(p.row.consumedAmount), width: 100 },
        { headerName: 'Remaining Amount', field: 'rem', align: 'right', renderCell: p => formatNumber(p.row.poAmount - p.row.consumedAmount), width: 100 },
        { headerName: 'Delivery Amount', field: 'deliveryAmount', align: 'right', renderCell: p => formatNumber(p.row.deliveryAmount), width: 100 },
        { headerName: 'Currency', field: 'currencyCode', width: 90 },
        {
            headerName: 'Client Name',
            field: 'clientName',
            width: 140,
            renderCell: p => {
                const value = p.row.clientName || '';
                return (
                    <Tooltip title={value} placement="top" disableHoverListener={!value}>
                        <Box sx={{ width: '100%', lineHeight: 1.2, whiteSpace: 'normal', overflowWrap: 'anywhere', display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 2, overflow: 'hidden', textOverflow: 'ellipsis', maxHeight: '2.4em' }}>
                            {value}
                        </Box>
                    </Tooltip>
                );
            }
        },
        { headerName: 'Job Code', field: 'jobCode', width: 100 },
        {
            headerName: 'Campaign Name',
            field: 'campaignName',
            width: 150,
            renderCell: p => {
                const value = p.row.campaignName || '';
                return (
                    <Tooltip title={value} placement="top" disableHoverListener={!value}>
                        <Box sx={{ width: '100%', lineHeight: 1.2, whiteSpace: 'normal', overflowWrap: 'anywhere', display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 2, overflow: 'hidden', textOverflow: 'ellipsis', maxHeight: '2.4em' }}>
                            {value}
                        </Box>
                    </Tooltip>
                );
            }
        },
        {
            headerName: 'Media Plan Name',
            field: 'mediaPlanName',
            width: 150,
            renderCell: p => {
                const value = p.row.mediaPlanName || '';
                return (
                    <Tooltip title={value} placement="top" disableHoverListener={!value}>
                        <Box sx={{ width: '100%', lineHeight: 1.2, whiteSpace: 'normal', overflowWrap: 'anywhere', display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 2, overflow: 'hidden', textOverflow: 'ellipsis', maxHeight: '2.4em' }}>
                            {value}
                        </Box>
                    </Tooltip>
                );
            }
        }
    ];

    const grandTotalAllocated = selectedIOs.reduce((sum, io) => sum + (io.allocAmount || 0), 0);

    const isValidationFailed = React.useMemo(() => {
        if (selectedIOs.length === 0) return true;
        if (sessionMode === 'io') {
            return selectedIOs.some(io => {
                const ioTolerance = io.tolerancePOFlag === 'N' ? (io.tolerancePOAmount || 0) : ((io.tolerancePOAmount || 0) * (io.poAmount || 0)) / 100;
                const ioMaxAllowedBudget = (io.poAmount || 0) + ioTolerance;
                return isBudgetFullyConsumed(ioMaxAllowedBudget - (io.consumedAmount || 0), io.poAmount, io.tolerancePOFlag);
            });
        }
        if (sessionMode === 'placement') {
            if (grandTotalAllocated === 0) return true;

            for (const io of selectedIOs) {
                // Must allocate a non-zero budget for selected IO
                if ((io.allocAmount || 0) === 0) return true;

                // Cannot exceed IO budget
                const ioTolerance = io.tolerancePOFlag === 'N' ? (io.tolerancePOAmount || 0) : ((io.tolerancePOAmount || 0) * (io.poAmount || 0)) / 100;
                const ioMaxAllowedBudget = (io.poAmount || 0) + ioTolerance;
                const ioTotalRequestedAmount = Number(((io.consumedAmount || 0) + (io.allocAmount || 0)).toFixed(2));
                if (isAmountOverBudget(ioTotalRequestedAmount, ioMaxAllowedBudget, io.poAmount, io.tolerancePOFlag)) return true;

                // Check individual placements ONLY when the IO is in placement mode
                if (io.mode === 'placement') {
                    for (const p of io.placements) {
                        const otherAlloc = getOtherLinesAllocated(io.poCode, p.placementId);
                        const tolerance = io.tolerancePOFlag === 'N' ? (io.tolerancePOAmount || 0) : ((io.tolerancePOAmount || 0) * (p.amount || 0)) / 100;
                        const maxAllowedBudget = (p.amount || 0) + tolerance;
                        const totalRequestedAmount = (p.consumedAmt || 0) + otherAlloc + (p.allocAmount || 0);

                        if ((p.allocAmount || 0) === 0) return true;
                        if (isAmountOverBudget(totalRequestedAmount, maxAllowedBudget, p.amount, io.tolerancePOFlag)) return true;
                    }
                }
            }
        }
        return false;
    }, [selectedIOs, sessionMode, grandTotalAllocated]);

    const getTooltipMessage = () => {
        if (selectedIOs.length === 0) return "Please select at least one item";
        if (sessionMode === 'io' && selectedIOs.some(io => {
            const ioTolerance = io.tolerancePOFlag === 'N' ? (io.tolerancePOAmount || 0) : ((io.tolerancePOAmount || 0) * (io.poAmount || 0)) / 100;
            const ioMaxAllowedBudget = (io.poAmount || 0) + ioTolerance;
            return isBudgetFullyConsumed(ioMaxAllowedBudget - (io.consumedAmount || 0), io.poAmount, io.tolerancePOFlag);
        })) {
            return "Cannot select fully consumed IO";
        }
        if (sessionMode === 'placement') {
            if (grandTotalAllocated === 0) return "Total placement allocation must be non-zero";

            for (const io of selectedIOs) {
                if ((io.allocAmount || 0) === 0) return "All selected IOs must have a non-zero allocated budget";
                const ioTolerance = io.tolerancePOFlag === 'N' ? (io.tolerancePOAmount || 0) : ((io.tolerancePOAmount || 0) * (io.poAmount || 0)) / 100;
                const ioMaxAllowedBudget = (io.poAmount || 0) + ioTolerance;
                const ioTotalRequestedAmount = Number(((io.consumedAmount || 0) + (io.allocAmount || 0)).toFixed(2));
                if (isAmountOverBudget(ioTotalRequestedAmount, ioMaxAllowedBudget, io.poAmount, io.tolerancePOFlag)) {
                    const ioRemWithTolerance = ioMaxAllowedBudget - (io.consumedAmount || 0);
                    return `Allocated amount cannot exceed the IO remaining budget (${formatNumber(ioRemWithTolerance)})`;
                }

                if (io.mode === 'placement') {
                    for (const p of io.placements) {
                        const otherAlloc = getOtherLinesAllocated(io.poCode, p.placementId);
                        const tolerance = io.tolerancePOFlag === 'N' ? (io.tolerancePOAmount || 0) : ((io.tolerancePOAmount || 0) * (p.amount || 0)) / 100;
                        const maxAllowedBudget = (p.amount || 0) + tolerance;
                        const totalRequestedAmount = (p.consumedAmt || 0) + otherAlloc + (p.allocAmount || 0);

                        if ((p.allocAmount || 0) === 0) return "Placement allocation must be non-zero";
                        if (isAmountOverBudget(totalRequestedAmount, maxAllowedBudget, p.amount, io.tolerancePOFlag)) {
                            const plRemWithTolerance = maxAllowedBudget - (p.consumedAmt || 0) - otherAlloc;
                            return `Allocated amount cannot exceed placement remaining budget (${formatNumber(plRemWithTolerance)})`;
                        }
                    }
                }
            }
        }
        return "";
    };

    const isIOModeActive = effectiveMode === 'io';
    const isPlacementModeActive = !!effectiveMode && effectiveMode !== 'io';
    const showPlacementDetails = selectedIOs.length > 0 && isPlacementModeActive;
    const selectedIOsForSave = selectedIOs.map(io =>
        normalizeMode(io.mode) === 'io'
            ? { ...io, placements: [], allocAmount: 0 }
            : io
    );
    const sessionModeForSave = sessionMode || normalizeMode(selectedIOsForSave[0]?.mode) || lockedToMode || null;


    const renderModeBanner = () => {
        const effectiveMode = sessionMode || lockedToMode;
        const isLockedByOtherLines = !sessionMode && !!lockedToMode;
        return (
            <Box sx={{ display: effectiveMode ? 'flex' : 'none', justifyContent: 'space-between', alignItems: 'center', p: '7px 14px', fontSize: '11px', borderBottom: '1px solid', bgcolor: effectiveMode === 'io' ? '#eff6ff' : '#fffbeb', borderColor: effectiveMode === 'io' ? '#bfdbfe' : '#fcd34d', color: effectiveMode === 'io' ? '#1d4ed8' : '#92400e' }}>
                <Stack direction="row" alignItems="center" gap={1}>
                    <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                    <Typography variant="caption" sx={{ fontSize: '11px' }}>
                        {info.isPartialPostMode
                            ? (effectiveMode === 'io'
                                ? <React.Fragment><strong>Matching by IO</strong> — checking the IO box submits all placements as one transaction. Mode is locked while this invoice is partially posted.</React.Fragment>
                                : <React.Fragment><strong>Matching by Placement</strong> — select individual placements per IO. Mode is locked while this invoice is partially posted.</React.Fragment>)
                            : isLockedByOtherLines
                                ? (effectiveMode === 'io'
                                    ? <React.Fragment>Other detail lines are using <strong>Matching by IO</strong>. To invoice by placement instead, clear all and reset first.</React.Fragment>
                                    : <React.Fragment>Other detail lines are using <strong>Matching by Placement</strong>. To invoice by IO instead, clear all and reset first.</React.Fragment>)
                                : (effectiveMode === 'io'
                                    ? <React.Fragment><strong>Matching by IO</strong> — checking the IO box submits all placements as one transaction. To invoice by placement instead, clear all and click a row.</React.Fragment>
                                    : <React.Fragment><strong>Matching by Placement</strong> — select individual placements per IO. To invoice by IO instead, clear all and check an IO box.</React.Fragment>)
                        }
                    </Typography>
                </Stack>
                <Button size="small" variant="outlined" color="inherit" disabled={!!info.isPartialPostMode || hasMixedApprovalStatus} sx={{ fontSize: '10px', p: '2px 8px', minWidth: 0, textTransform: 'none', borderColor: 'currentColor' }} onClick={handleReset}>Clear &amp; Reset</Button>
            </Box>
        );
    };

    return (
        <>
            <Dialog
                open={true}
                TransitionComponent={Transition}
                TransitionProps={{
                    onEntered: () => {
                        alignIOGridLayout();
                    }
                }}
                fullWidth
                maxWidth="xl"
                PaperProps={{ sx: { height: '90vh' } }}
            >
                <DialogTitle sx={{ p: '12px 8px 12px 22px', borderBottom: '1px solid #e8eaed', bgcolor: '#fff' }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Box>
                            <Typography sx={{ fontSize: '14px', fontWeight: 600, display: 'inline-block', mr: 2 }}>Select IO/PO Number</Typography>
                            <Typography sx={{ fontSize: '12px', color: '#6b7280', display: 'inline-block' }}>Vendor: {info.vendorCode} : {info.vendorName || ''}</Typography>
                        </Box>
                        <IconButton onClick={() => onClose(null)} size="small" sx={{ color: '#9ca3af', '&:hover': { bgcolor: '#f3f4f6' } }}><CloseIcon /></IconButton>
                    </Stack>
                </DialogTitle>

                <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', bgcolor: '#f0f2f5', overflow: 'hidden' }}>
                    <Box ref={splitContainerRef} sx={{ flex: 1, width: '100%', minWidth: 0, overflowX: 'hidden', overflowY: 'hidden', display: 'flex', flexDirection: 'row' }}>
                        {/* LEFT PANE (IO/PO list) - fully collapsed (not rendered) whenever the
                        Placement(s) panel is showing. The IO panel and Placement(s) panel are
                        mutually exclusive and never both rendered at the same time (no split view). */}
                        {!(showPlacementDetails && !showIOPanel) && (
                            <Box sx={{
                                display: 'flex', flexDirection: 'column', bgcolor: '#fff', borderRight: '1px solid #e8eaed', height: '100%', width: '100%', minWidth: 0,
                                flex: '1 1 100%'
                            }}>

                                {/* Banner */}
                                {/* Show whenever a local selection mode is active OR another detail line
                            has already locked the document to a mode. Without the lockedToMode
                            check here, removing the local selection (sessionMode -> null) would hide
                            this banner - and its "Clear all & reset" button - even though the user is
                            still locked out of the other selection method by other lines. */}
                                {renderModeBanner()}

                                {/* Toolbar / Legend */}
                                <Box sx={{ p: '9px 14px 9px 22px', borderBottom: '1px solid #e8eaed', bgcolor: '#f8f9fb', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', rowGap: 0.75 }}>
                                    <Stack direction="row" gap={2} flexWrap="wrap" useFlexGap>
                                        <Stack direction="row" alignItems="center" gap={0.5}>
                                            <Box sx={{ width: 10, height: 10, borderRadius: '3px', bgcolor: '#2563eb' }} />
                                            <Typography sx={{ fontSize: '11px', color: '#6b7280' }}><strong>Select the checkbox to match using the IO Summary.</strong> </Typography>
                                        </Stack>
                                        <Stack direction="row" alignItems="center" gap={0.5}>
                                            <Box sx={{ width: 10, height: 10, borderRadius: '3px', bgcolor: '#f59e0b' }} />
                                            <Typography sx={{ fontSize: '11px', color: '#6b7280' }}><strong>Click IO/PO Number and select placements to match.</strong> </Typography>
                                        </Stack>
                                    </Stack>
                                </Box>

                                <Box ref={ioGridHostRef} sx={{ flex: 1, minHeight: 0, width: '100%', minWidth: 0 }}>
                                    <EnhancedDataGrid rows={ioData} columns={columns} getRowId={r => r.poCode} onRowClick={handleRowClick} disableSelectionOnClick
                                        apiRef={ioGridApiRef}
                                        pagination
                                        pageSize={10}
                                        disableVirtualization
                                        gridRef={ioGridRef}
                                        leftCustomComponent={getIncludeAllIO()}
                                        customcomponent={
                                            <Tooltip title="Pull data by IO/PO Code">
                                                <IconButton size="small" onClick={handleRefresh} sx={{ float: 'right', zIndex: 1, marginTop: '10px' }}>
                                                    <RefreshIcon />
                                                </IconButton>
                                            </Tooltip>
                                        }
                                        sx={{ '& .MuiDataGrid-row': { cursor: isIOModeActive ? 'not-allowed' : 'pointer' } }} />
                                </Box>
                            </Box>
                        )}

                        {/* RIGHT PANE (8) - Placement(s) panel. Renders in place of the IO panel
                        (never alongside it - no splitter/split view is supported anymore). */}
                        {showPlacementDetails && !showIOPanel && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', bgcolor: '#f8f9fb', height: '100%', width: '100%', minWidth: 0, overflow: 'hidden', flex: '1 1 100%' }}>
                                {renderModeBanner()}
                                <Box sx={{ p: '10px 16px', borderBottom: '1px solid #e8eaed', bgcolor: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Stack direction="row" alignItems="center" gap={1}>
                                        <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#1a1a2e' }}>Placement(s)</Typography>
                                    </Stack>

                                </Box>

                                <Box sx={{ flex: 1, overflowY: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    {selectedIOs.map(io => {
                                        const allPlacementsForIO = placementsCache[io.poCode] || [];
                                        const ioPlacementsList = showAllPlacements
                                            ? allPlacementsForIO
                                            : allPlacementsForIO.filter(p => (p.amount - (p.consumedAmt || 0)) > 0);
                                        const selectablePlacements = ioPlacementsList;
                                        const ioBudget = parseFloat(io.poAmount) || 0;
                                        const consumedAmount = parseFloat(io.consumedAmount) || 0;
                                        const allocatedAmount = parseFloat(io.allocAmount) || 0;
                                        const totalUsedAmount = consumedAmount + allocatedAmount;
                                        const consumedPct = ioBudget > 0 ? Math.min((consumedAmount / ioBudget) * 100, 100) : 0;
                                        const allocatedPctRaw = ioBudget > 0 ? Math.min((allocatedAmount / ioBudget) * 100, 100) : 0;
                                        const allocatedPct = Math.min(allocatedPctRaw, Math.max(0, 100 - consumedPct));
                                        const ioTolerance = io.tolerancePOFlag === 'N' ? (io.tolerancePOAmount || 0) : ((io.tolerancePOAmount || 0) * (io.poAmount || 0)) / 100;
                                        const ioMaxAllowedBudget = ioBudget + ioTolerance;
                                        const isOverBudget = isAmountOverBudget(totalUsedAmount, ioMaxAllowedBudget, ioBudget, io.tolerancePOFlag);
                                        const remainingAmount = ioBudget - totalUsedAmount;

                                        const selectablePlacementIds = new Set(selectablePlacements.map(p => p.placementId));
                                        const checkedCount = io.placements.filter(p => selectablePlacementIds.has(p.placementId)).length;
                                        const allSelectablePlacementsChecked = selectablePlacements.length > 0 && checkedCount === selectablePlacements.length;
                                        let selStateText = io.mode === 'io' ? "All placements — invoiced by IO" :
                                            selectablePlacements.length === 0 ? "No Placements Available" :
                                                checkedCount === 0 ? "No Placements Selected" :
                                                    allSelectablePlacementsChecked ? `All ${selectablePlacements.length} Placements Selected` :
                                                        `${checkedCount} of ${selectablePlacements.length} Placements Selected`;

                                        const selStateBg = io.mode === 'io' ? '#ecfdf5' : (checkedCount === 0 ? '#f3f4f6' : (checkedCount < selectablePlacements.length ? '#fffbeb' : '#ecfdf5'));
                                        const selStateColor = io.mode === 'io' ? '#047857' : (checkedCount === 0 ? '#6b7280' : (checkedCount < selectablePlacements.length ? '#92400e' : '#047857'));
                                        const selStateBorder = io.mode === 'io' ? '#a7f3d0' : (checkedCount === 0 ? '#e5e7eb' : (checkedCount < selectablePlacements.length ? '#fcd34d' : '#a7f3d0'));
                                        const cardAccentColor = '#7d8796';

                                        const isIOLocked = lockedIOs.has(io.poCode);
                                        const isIOPosted = !!(info.isPartialPostMode && info.postedIONumbers?.has(io.poCode));

                                        return (
                                            <Paper key={io.poCode} variant="outlined" sx={{ flexShrink: 0, borderRadius: '8px', overflow: 'hidden', borderColor: cardAccentColor, borderTop: `1px solid ${cardAccentColor}` }}>

                                                {/* Block Header */}
                                                <Box sx={{ p: '10px 14px', borderBottom: '1px solid #e8eaed', bgcolor: '#fff' }}>
                                                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1}>
                                                        <Stack direction="row" gap={1} alignItems="center">
                                                            <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#1a1a2e' }}>{io.poCode}</Typography>
                                                            <Box sx={{ fontSize: '10px', fontWeight: 500, borderRadius: '20px', px: '9px', py: '2px', border: '1px solid', bgcolor: selStateBg, color: selStateColor, borderColor: selStateBorder }}>
                                                                {selStateText}
                                                            </Box>
                                                            {isIOPosted && (
                                                                <Box sx={{ fontSize: '10px', fontWeight: 600, borderRadius: '20px', px: '9px', py: '2px', border: '1px solid #a7f3d0', bgcolor: '#ecfdf5', color: '#047857' }}>
                                                                    Posted
                                                                </Box>
                                                            )}
                                                        </Stack>
                                                        <Stack direction="row" alignItems="center" gap={1}>
                                                            <Stack direction="row" alignItems="center" gap={0.5}>
                                                                <Checkbox id="showAllPlacementsCheckbox" size="small" checked={showAllPlacements} onChange={e => setShowAllPlacements(e.target.checked)} sx={{ p: '2px' }} />
                                                                <Typography sx={{ fontSize: '11px', color: '#6b7280', userSelect: 'none', cursor: 'pointer' }} onClick={() => setShowAllPlacements(prev => !prev)}>Show All</Typography>
                                                            </Stack>
                                                            {!isIOPosted && (
                                                                <Button
                                                                    size="small"
                                                                    variant="outlined"
                                                                    onClick={() => removeIO(io.poCode)}
                                                                    startIcon={<CloseIcon sx={{ fontSize: '13px !important' }} />}
                                                                    sx={{
                                                                        ml: 1,
                                                                        minWidth: 0,
                                                                        bgcolor: '#fff1f0',
                                                                        color: '#b42318',
                                                                        borderColor: '#fda29b',
                                                                        fontSize: '11px',
                                                                        fontWeight: 700,
                                                                        textTransform: 'none',
                                                                        py: 0.45,
                                                                        px: 1.2,
                                                                        borderRadius: '6px',
                                                                        boxShadow: '0 1px 2px rgba(16,24,40,0.04)',
                                                                        transition: 'all 0.15s ease-in-out',
                                                                        '& .MuiButton-startIcon': {
                                                                            marginRight: '4px',
                                                                            marginLeft: '-2px'
                                                                        },
                                                                        '&:hover': {
                                                                            bgcolor: '#fee4e2',
                                                                            borderColor: '#f97066',
                                                                            color: '#912018',
                                                                            boxShadow: '0 2px 4px rgba(145,32,24,0.12)'
                                                                        }
                                                                    }}
                                                                >
                                                                    Remove
                                                                </Button>
                                                            )}
                                                        </Stack>
                                                    </Stack>

                                                    <Stack direction="row" flexWrap="wrap" gap={0.5}>
                                                        {[
                                                            { label: 'Client', val: io.clientName },
                                                            { label: 'Job Code', val: io.jobCode },
                                                            { label: 'Campaign', val: io.campaignName },
                                                            { label: 'Media Plan', val: io.mediaPlanName },
                                                            { label: 'IO Budget', val: formatNumber(io.poAmount) },
                                                            { label: 'Remaining Amount', val: formatNumber(remainingAmount) }
                                                        ].map((meta, idx) => (
                                                            <Box key={idx} sx={{ display: 'flex', alignItems: 'center', bgcolor: '#ffffff', border: '1px solid #d1d5db', borderRadius: '5px', overflow: 'hidden' }}>
                                                                <Typography sx={{ px: 1.25, py: 0.6, fontSize: '12px', fontWeight: 500, textTransform: 'none', color: '#334155', bgcolor: '#e5e7eb', borderRight: '1px solid #d1d5db', lineHeight: 1.3 }}>{meta.label}</Typography>
                                                                <Typography sx={{ px: 1.25, py: 0.6, fontSize: '12px', fontWeight: 500, color: '#111827', lineHeight: 1.3 }}>{meta.val || '-'}</Typography>
                                                            </Box>
                                                        ))}
                                                    </Stack>
                                                </Box>

                                                {/* Budget Bar */}
                                                <Box sx={{ p: '8px 14px 10px', borderBottom: '1px solid #e8eaed', bgcolor: '#fff' }}>
                                                    <Stack direction="row" justifyContent="space-between" mb={0.5}>
                                                        <Typography sx={{ fontSize: '10px', color: '#6b7280' }}>Budget Allocation</Typography>
                                                    </Stack>
                                                    <Box sx={{ height: 6, bgcolor: '#e8eaed', borderRadius: 10, overflow: 'hidden', position: 'relative' }}>
                                                        <Box sx={{ height: '100%', width: `${consumedPct}%`, bgcolor: '#b45309', transition: 'width 0.3s ease', position: 'absolute', left: 0, top: 0 }} />
                                                        <Box sx={{ height: '100%', width: `${allocatedPct}%`, bgcolor: isOverBudget ? '#ef4444' : '#2563eb', transition: 'width 0.3s ease', position: 'absolute', left: `${consumedPct}%`, top: 0 }} />
                                                    </Box>
                                                    <Stack direction="row" justifyContent="space-between" mt={0.5}>
                                                        <Stack direction="row" alignItems="center" gap={1}>
                                                            <Typography sx={{ fontSize: '10px', color: '#92400e', fontWeight: 700 }}>{formatNumber(consumedAmount)} Already Consumed</Typography>
                                                            <Typography sx={{ fontSize: '10px', color: '#2563eb', fontWeight: 600 }}>{formatNumber(allocatedAmount)} Current Invoice</Typography>
                                                        </Stack>
                                                        <Typography sx={{ fontSize: '10px', color: isOverBudget ? '#ef4444' : '#6b7280', fontWeight: isOverBudget ? 600 : 400 }}>
                                                            {isOverBudget ? `${formatNumber(totalUsedAmount - ioBudget)} Over Budget` : ``}
                                                        </Typography>
                                                    </Stack>
                                                </Box>

                                                {/* Placement Table */}
                                                {allPlacementsForIO.length === 0 ? (
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: '28px 14px', gap: 0.5 }}>
                                                        <InfoOutlinedIcon sx={{ fontSize: 22, color: '#9ca3af' }} />
                                                        <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#6b7280' }}>
                                                            This IO/PO has no placements
                                                        </Typography>
                                                        <Typography sx={{ fontSize: '11px', color: '#9ca3af' }}>
                                                            Invoicing will be applied at the IO level only.
                                                        </Typography>
                                                    </Box>
                                                ) : (
                                                    <TableContainer sx={{ maxHeight: 350 }}>
                                                        <Table size="small" stickyHeader sx={{ '& th': { fontSize: '12px', fontWeight: 500, textTransform: 'none', color: '#374151', bgcolor: '#f8f9fb', p: '9px 8px', whiteSpace: 'nowrap' }, '& td': { fontSize: '12px', fontWeight: 500, color: '#111827', p: '8px 8px', whiteSpace: 'nowrap', borderBottom: '1px solid #f0f2f5' } }}>
                                                            <TableHead>
                                                                <TableRow>
                                                                    <TableCell width={40}>
                                                                        {!io.mode || io.mode === 'placement' ? (
                                                                            <Checkbox id={`selectAllPlacementsCheckbox-${io.poCode}`} size="small" sx={{ p: 0 }}
                                                                                 checked={allSelectablePlacementsChecked}
                                                                                 indeterminate={checkedCount > 0 && !allSelectablePlacementsChecked}
                                                                                disabled={isIOLocked || !canEditAmountByRule || selectablePlacements.length === 0}
                                                                                onChange={(e) => toggleAllPlacements(io.poCode, e.target.checked)} />
                                                                        ) : null}
                                                                    </TableCell>
                                                                    <TableCell>Placement ID</TableCell>
                                                                    <TableCell>{getDisplayName('site')}</TableCell>
                                                                    <TableCell>Placement Name</TableCell>
                                                                    <TableCell>Start Date</TableCell>
                                                                    <TableCell>End Date</TableCell>
                                                                    <TableCell>Cost Type</TableCell>
                                                                    <TableCell align="right">Quantity</TableCell>
                                                                    <TableCell align="right">Rate</TableCell>
                                                                    <TableCell align="right">IO Amount</TableCell>
                                                                    <TableCell align="right">Consumed Amt</TableCell>
                                                                    <TableCell align="right">Remaining Amt</TableCell>
                                                                    <TableCell align="right">Amount</TableCell>
                                                                </TableRow>
                                                            </TableHead>
                                                            <TableBody>
                                                                {ioPlacementsList.map(p => {
                                                                    const isConsumed = (p.amount - (p.consumedAmt || 0)) <= 0;
                                                                    const isChecked = io.mode === 'io' || io.placements.some(sel => sel.placementId === p.placementId);
                                                                    const selData = io.placements.find(sel => sel.placementId === p.placementId) || {};
                                                                    const otherAlloc = getOtherLinesAllocated(io.poCode, p.placementId);
                                                                    const plRem = (p.amount - (p.consumedAmt || 0)) - otherAlloc;
                                                                    const plTolerance = io.tolerancePOFlag === 'N' ? (io.tolerancePOAmount || 0) : ((io.tolerancePOAmount || 0) * (p.amount || 0)) / 100;
                                                                    const plMaxAllowedBudget = (p.amount || 0) + plTolerance;
                                                                    const plCurrentAlloc = isChecked ? (selData.allocAmount !== undefined ? parseFloat(selData.allocAmount) || 0 : plRem) : 0;
                                                                    const plMaxAlloc = Number((plMaxAllowedBudget - (p.consumedAmt || 0) - otherAlloc).toFixed(2));
                                                                    const isPlacementZeroAmount = isChecked && io.mode === 'placement' && plCurrentAlloc === 0;
                                                                    const isPlacementExceedsBudget = isChecked && io.mode === 'placement' && plCurrentAlloc > 0 && plCurrentAlloc > plMaxAlloc;
                                                                    const isPlacementOverBudget = isPlacementZeroAmount || isPlacementExceedsBudget;
                                                                    const placementErrorTooltip = isPlacementZeroAmount
                                                                        ? 'Allocated amount must be non-zero'
                                                                        : (isPlacementExceedsBudget
                                                                            ? `Allocated amount cannot exceed placement remaining budget (${formatNumber(plMaxAlloc)})`
                                                                            : '');

                                                                    return (
                                                                        <TableRow key={p.placementId} hover sx={{ bgcolor: isConsumed ? '#f3f4f6' : (isChecked ? '#f0f9ff' : 'inherit'), '& td': { color: isConsumed ? '#9ca3af' : '#374151' } }}>
                                                                            <TableCell>
                                                                                <Checkbox id={`placementCheckbox-${io.poCode}-${p.placementId}`} size="small" sx={{ p: 0 }}
                                                                                    checked={isChecked}
                                                                                    disabled={isIOLocked || io.mode === 'io' || !canEditAmountByRule}
                                                                                    onChange={(e) => togglePlacement(io.poCode, p, e.target.checked)} />
                                                                            </TableCell>
                                                                            <TableCell sx={{ color: '#2563eb !important', fontWeight: 500 }}>{p.placementId || p.placementCode}</TableCell>
                                                                            <TableCell>{p.siteName || ''}</TableCell>
                                                                            <TableCell sx={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' }} title={p.placementName}>{p.placementName}</TableCell>
                                                                            <TableCell sx={{ color: '#6b7280 !important' }}>{fDate(p.startDate)}</TableCell>
                                                                            <TableCell sx={{ color: '#6b7280 !important' }}>{fDate(p.endDate)}</TableCell>
                                                                            <TableCell>
                                                                                <Box sx={{ display: 'inline-block', borderRadius: '4px', px: '7px', py: '1px', fontSize: '10px', fontWeight: 500, border: '1px solid', ...((p.costMethod || p.costType) === 'CPM' ? { bgcolor: '#ecfdf5', color: '#047857', borderColor: '#a7f3d0' } : { bgcolor: '#fffbeb', color: '#92400e', borderColor: '#fcd34d' }) }}>
                                                                                    {p.costMethod || p.costType || 'Flat'}
                                                                                </Box>
                                                                            </TableCell>
                                                                            <TableCell align="right">{p.quantity?.toLocaleString()}</TableCell>
                                                                            <TableCell align="right">{formatNumber(p.rate)}</TableCell>
                                                                            <TableCell align="right" sx={{ fontWeight: 500 }}>{formatNumber(p.amount)}</TableCell>
                                                                            <TableCell align="right" sx={{ color: !p.consumedAmt ? '#9ca3af !important' : 'inherit' }}>{formatNumber(p.consumedAmt || 0)}</TableCell>
                                                                            <TableCell align="right" sx={{ color: (plRem - plCurrentAlloc) > 0 ? '#047857 !important' : '#9ca3af !important', fontWeight: (plRem - plCurrentAlloc) > 0 ? 500 : 400 }}>{formatNumber(plRem - plCurrentAlloc)}</TableCell>
                                                                            <TableCell align="right">
                                                                                <AllocAmountField
                                                                                    key={`${p.placementId}_${isChecked}`}
                                                                                    id={`allocAmount-${io.poCode}-${p.placementId}`}
                                                                                    value={isChecked ? (selData.allocAmount !== undefined ? selData.allocAmount : plRem) : 0}
                                                                                    onCommit={(numericVal) => handleallocAmountChange(io.poCode, p.placementId, numericVal)}
                                                                                    disabled={!isChecked || isIOLocked || info.disallowPartialPlacementConsumption === 1 || !canEditAmountByRule}
                                                                                    max={Math.max(0, plMaxAlloc)}
                                                                                    error={isPlacementOverBudget}
                                                                                    tooltipTitle={placementErrorTooltip}
                                                                                />
                                                                            </TableCell>
                                                                        </TableRow>
                                                                    );
                                                                })}
                                                            </TableBody>
                                                        </Table>
                                                    </TableContainer>
                                                )}

                                                {/* Block Footer */}
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: '8px 14px', borderTop: '1px solid #f0f2f5', bgcolor: '#f8f9fb' }}>
                                                    <Typography sx={{ fontSize: '11px', color: '#6b7280' }}>
                                                        {allPlacementsForIO.length === 0 ? 'No placements available for this IO/PO' : (io.mode === 'io' ? `Invoicing entire IO (${ioPlacementsList.length} placements)` : (selectablePlacements.length === 0 ? 'No Placements Available' : `${checkedCount} of ${selectablePlacements.length} Placement(s) Selected`))}
                                                    </Typography>
                                                </Box>
                                            </Paper>
                                        );
                                    })}
                                </Box>

                                {/* Grand Total Bar */}
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 2, p: '10px 16px', borderTop: '2px solid #e8eaed', bgcolor: '#fff', flexShrink: 0 }}>
                                    <Typography sx={{ fontSize: '12px', fontWeight: 500, color: '#6b7280' }}>Total Amount</Typography>
                                    <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#1a1a2e', minWidth: 110, textAlign: 'right' }}>{formatNumber(grandTotalAllocated)}</Typography>
                                </Box>
                            </Box>
                        )}
                    </Box>
                </DialogContent>

                <DialogActions sx={{ p: '11px 22px', bgcolor: '#fafafa', borderTop: '1px solid #e8eaed' }}>
                    <Tooltip title={getTooltipMessage()} placement="top">
                        <span>
                            <Button onClick={() => onClose(null, selectedIOsForSave, sessionModeForSave)} variant="contained" color="primary" disabled={isValidationFailed} sx={{ fontSize: '12px', fontWeight: 500, textTransform: 'none', px: 3 }}>Select</Button>
                        </span>
                    </Tooltip>
                    <Button onClick={() => onClose(null)} variant="outlined" sx={{ fontSize: '12px', fontWeight: 500, textTransform: 'none', px: 3 }}>Cancel</Button>
                </DialogActions>
            </Dialog>
            <GenericMessageDialog
                type={'confirm'}
                open={openConfirm}
                onClose={() => { }}
                message={"Are you sure, you want to uncheck 'Include All IO/PO'? Selected IOs outside the invoice's service period will be removed."}
                acceptCallback={confirmDisableIncludeAllPO}
                rejectCallback={cancelDisableIncludeAllPO}
                maxWidthPx={400}
            />
            <GenericMessageDialog
                type={'confirm'}
                open={openResetAllConfirm}
                onClose={() => { }}
                header={
                    <Stack direction="row" alignItems="center" gap={1.25}>
                        <Box sx={{
                            width: 40, height: 40, borderRadius: '10px',
                            bgcolor: '#fef3c7', color: '#d97706',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                        }}>
                            <WarningAmberRoundedIcon sx={{ fontSize: 22 }} />
                        </Box>
                        <Typography sx={{ fontSize: '15px', fontWeight: 700, color: '#1a1a2e' }}>
                            Clear all IO/PO &amp; mode?
                        </Typography>
                    </Stack>
                }
                message={
                    <Box sx={{ pl: '52px', pr: 1, pb: 0.5 }}>
                        <Typography sx={{ fontSize: '13px', color: '#4b5563', lineHeight: 1.6 }}>
                            This will remove the <strong>IO/PO Number</strong> for
                            {' '}<strong>every detail line</strong> on this invoice.
                        </Typography>
                        <Box sx={{
                            mt: 1.5, p: '10px 12px', borderRadius: '8px',
                            bgcolor: '#fffbeb', border: '1px solid #fde68a',
                            display: 'flex', alignItems: 'flex-start', gap: 1
                        }}>
                            <InfoOutlinedIcon sx={{ fontSize: 16, color: '#d97706', mt: '1px' }} />
                            <Typography sx={{ fontSize: '12px', color: '#92400e', fontWeight: 500 }}>
                                You will need to select the IO/PO Number again for every line. Do you want to continue?.
                            </Typography>
                        </Box>
                    </Box>
                }
                acceptCallback={confirmResetAllLinesMode}
                rejectCallback={cancelResetAllLinesMode}
                yesText="Yes, Clear All"
                noText="Cancel"
                maxWidthPx={440}
            />
        </>
    );
}